import { useState, useRef, useEffect, useCallback, type ChangeEvent } from 'react'
import { Button, Paragraph, Popup, Select } from '@quickmd-llc/design-system'
import type { License, LicenseVisitType } from '../license-form/transformLicense'
import { getStateName } from '../getStateName'
import { visibilityTypes } from '../Constants'

interface LicenseCardProps {
  license: License
  serviceTypes: { VisitTypeId: string; Name: string }[]
  onEdit: (license: License) => void
  onDelete: (license: License) => void
  onVisibilityChange?: (license: License, visibility: string) => void
}

/** Gap 14: Filter out visit types that are hidden at the base level (none/deeplink).
 *  Only check BaseVisibility — VisibilityOverride 'none' means "No bookings" (user-set). */
function getVisibleVisitTypes(visitTypes: LicenseVisitType[]): LicenseVisitType[] {
  return visitTypes.filter((vt) => {
    const base = vt.BaseVisibility
    return base !== 'none' && base !== 'deeplink'
  })
}

/** Gap 13: Fixed — no more duplicated "Name (Name)" */
function getVisitTypeNames(
  visitTypes: LicenseVisitType[],
  serviceTypes: { VisitTypeId: string; Name: string }[],
): string {
  return visitTypes
    .map((vt) => {
      const found = serviceTypes.find((st) => st.VisitTypeId === vt.VisitTypeId)
      return found ? found.Name : vt.VisitTypeId
    })
    .join(', ')
}

function getCurrentVisibility(visitTypes: LicenseVisitType[]): string {
  if (!visitTypes.length) return 'all'
  const firstVt = visitTypes[0]
  const first = firstVt?.VisibilityOverride ?? firstVt?.BaseVisibility ?? 'all'
  const allSame = visitTypes.every((vt) => (vt.VisibilityOverride ?? vt.BaseVisibility ?? 'all') === first)
  if (!allSame) return 'custom'
  return first
}

export function LicenseCard({ license, serviceTypes, onEdit, onDelete, onVisibilityChange }: LicenseCardProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [pendingVisibility, setPendingVisibility] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const stateName = getStateName(license.StateCode)
  const visibleVisitTypes = getVisibleVisitTypes(license.VisitTypes)
  const currentVis = getCurrentVisibility(visibleVisitTypes)

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setMenuOpen(false)
    }
  }, [])

  useEffect(() => {
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen, handleClickOutside])

  /** Gap 11: Show confirmation before changing visibility */
  function handleVisibilitySelect(e: ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value
    if (value === currentVis) return
    setPendingVisibility(value)
  }

  function confirmVisibilityChange() {
    if (pendingVisibility) {
      onVisibilityChange?.(license, pendingVisibility)
    }
    setPendingVisibility(null)
  }

  function cancelVisibilityChange() {
    setPendingVisibility(null)
  }

  return (
    <div className='overflow-hidden rounded-lg border border-shade-10 bg-white'>
      {/* Header panel */}
      <div className='flex items-center gap-sp-2 bg-white p-sp-2'>
        <p className='flex-1 text-base font-semibold tracking-[0.12px] text-text-dark'>
          <span>{stateName} </span>
          <span className='text-text-light'>{license.StateCode}</span>
        </p>

        <Select value={pendingVisibility ?? currentVis} onChange={handleVisibilitySelect}>
          {currentVis === 'custom' && (
            <option value='custom' disabled>
              Custom
            </option>
          )}
          <option value={visibilityTypes.allPatients.name}>{visibilityTypes.allPatients.displayName}</option>
          <option value={visibilityTypes.repeatPatients.name}>{visibilityTypes.repeatPatients.displayName}</option>
          <option value={visibilityTypes.noBookings.name}>{visibilityTypes.noBookings.displayName}</option>
        </Select>

        <div className='relative' ref={menuRef}>
          <button
            type='button'
            className='flex h-8 w-8 items-center justify-center rounded-md p-1 hover:bg-shade-5'
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label='License actions'>
            <svg width='4' height='16' viewBox='0 0 4 16' fill='currentColor' className='text-text-dark'>
              <circle cx='2' cy='2' r='1.75' />
              <circle cx='2' cy='8' r='1.75' />
              <circle cx='2' cy='14' r='1.75' />
            </svg>
          </button>
          {menuOpen ?
            <div className='absolute right-0 top-full z-10 mt-1 min-w-[120px] overflow-hidden rounded-lg border border-border-dark bg-white shadow-md'>
              <button
                type='button'
                className='w-full px-sp-2 py-sp-1 text-left text-sm text-text-dark first:rounded-t-md hover:bg-shade-5'
                onClick={() => {
                  setMenuOpen(false)
                  onEdit(license)
                }}>
                Edit
              </button>
              <button
                type='button'
                className='w-full px-sp-2 py-sp-1 text-left text-sm text-red-600 last:rounded-b-md hover:bg-shade-5'
                onClick={() => {
                  setMenuOpen(false)
                  onDelete(license)
                }}>
                Delete
              </button>
            </div>
          : null}
        </div>
      </div>

      {/* Divider */}
      <div className='h-px bg-shade-10' />

      {/* Body panel */}
      <div className='flex flex-col gap-sp-3 p-sp-2'>
        {/* Medical license (always present) */}
        <div className='flex items-center gap-sp-3'>
          <div className='flex flex-col gap-sp-1'>
            <span className='text-xs tracking-[0.14px] text-text-medium'>License type</span>
            <span className='text-base tracking-[0.12px] text-text-dark'>Medical</span>
          </div>
          <div className='flex flex-col gap-sp-1'>
            <span className='text-xs tracking-[0.14px] text-text-medium'>License no.</span>
            <span className='text-base tracking-[0.12px] text-text-dark'>{license.Number}</span>
          </div>
        </div>

        {/* CSL (if present) */}
        {license.CslNumber ?
          <div className='flex items-center gap-sp-3'>
            <div className='flex flex-col gap-sp-1'>
              <span className='text-xs tracking-[0.14px] text-text-medium'>License type</span>
              <span className='text-base tracking-[0.12px] text-text-dark'>CSL</span>
            </div>
            <div className='flex flex-col gap-sp-1'>
              <span className='text-xs tracking-[0.14px] text-text-medium'>License no.</span>
              <span className='text-base tracking-[0.12px] text-text-dark'>{license.CslNumber}</span>
            </div>
          </div>
        : null}

        {/* DEA (if present) */}
        {license.DeaNumber ?
          <div className='flex items-center gap-sp-3'>
            <div className='flex flex-col gap-sp-1'>
              <span className='text-xs tracking-[0.14px] text-text-medium'>License type</span>
              <span className='text-base tracking-[0.12px] text-text-dark'>DEA</span>
            </div>
            <div className='flex flex-col gap-sp-1'>
              <span className='text-xs tracking-[0.14px] text-text-medium'>License no.</span>
              <span className='text-base tracking-[0.12px] text-text-dark'>{license.DeaNumber}</span>
            </div>
            {license.DeaStateCode ?
              <div className='flex flex-col gap-sp-1'>
                <span className='text-xs tracking-[0.14px] text-text-medium'>State</span>
                <span className='text-base tracking-[0.12px] text-text-dark'>{getStateName(license.DeaStateCode)}</span>
              </div>
            : null}
          </div>
        : null}

        {/* Visit types */}
        {visibleVisitTypes.length > 0 && (
          <div className='flex flex-col gap-sp-1'>
            <span className='text-xs uppercase tracking-[0.14px] text-text-medium'>Visit type(s)</span>
            <Paragraph className='text-text-medium'>{getVisitTypeNames(visibleVisitTypes, serviceTypes)}</Paragraph>
          </div>
        )}
      </div>

      {/* Gap 11: Visibility change confirmation popup */}
      {pendingVisibility ?
        <Popup
          isOpen
          onClose={cancelVisibilityChange}
          header={{ title: 'Are you sure?' }}
          body={
            <Paragraph>
              This change in booking access will apply to all visit types for this provider in {stateName}.
            </Paragraph>
          }
          footer={
            <div className='flex justify-end gap-sp-1'>
              <Button priority='secondary' onClick={cancelVisibilityChange}>
                No, nevermind
              </Button>
              <Button priority='primary' onClick={confirmVisibilityChange}>
                Yes
              </Button>
            </div>
          }
        />
      : null}
    </div>
  )
}
